<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router';
import { computed, onMounted, onUnmounted } from 'vue';
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import NavBar from '@/components/NavBar.vue';
import { useDarkMode } from '@/composables/useDarkMode';
import { useAuthStore } from '@/stores/auth.store';
import { useToast } from 'primevue/usetoast';

const route = useRoute();
const toast = useToast();
const authStore = useAuthStore();
authStore.init();
const hideNavRoutes = ['/welcome', '/login'];
const showNav = computed(() => !hideNavRoutes.includes(route.path));
useDarkMode();

// Listen for foreground push notifications
function handlePushNotification(e: Event) {
  const { title, body } = (e as CustomEvent).detail;
  toast.add({
    severity: 'info',
    summary: title,
    detail: body,
    life: 6000,
  });
}
onMounted(() => window.addEventListener('inflection:push', handlePushNotification));
onUnmounted(() => window.removeEventListener('inflection:push', handlePushNotification));

// Apply saved font scale on load
const savedScale = localStorage.getItem('16p-font-scale');
if (savedScale) document.documentElement.style.fontSize = `${savedScale}%`;
</script>

<template>
  <Toast position="top-right" />
  <ConfirmDialog />
  <div :class="showNav ? 'pb-20' : ''" class="page-bg min-h-screen">
    <RouterView />
  </div>
  <NavBar v-if="showNav" />
</template>

<style>
/* ── Inflection Design System ──────────────────────────────── */

:root {
  --font-brand: 'Outfit', sans-serif;
  --color-brand: #6c5ce7;
  --color-bg: #0b0b0f;
  --disc-red: #e74c3c;
  --disc-yellow: #f1c40f;
  --disc-green: #2ecc71;
  --disc-blue: #3498db;
}

/* Base */
html {
  background: #0b0b0f;
}
html.light {
  background: #f8f9fa;
}
body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: #0b0b0f;
}
html.light body {
  background: #f8f9fa;
  color: #111827;
}

/* Subtle texture layer — constellation pattern at very low opacity */
body::before {
  content: '';
  position: fixed;
  inset: 0;
  background-image: url('/images/brand-pattern.png');
  background-size: 400px;
  opacity: 0.015;
  pointer-events: none;
  z-index: 0;
}

/* Page gradient — soft colour wash at the top of each page */
.page-glow {
  position: relative;
}
.page-glow::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 300px;
  pointer-events: none;
  border-radius: 0 0 50% 50%;
  filter: blur(80px);
  opacity: 0.07;
  z-index: 0;
}
.page-glow > * { position: relative; z-index: 1; }

/* DISC-coloured page glows */
.page-glow--purple::before { background: radial-gradient(ellipse at 50% 0%, #6c5ce7, transparent 70%); }
.page-glow--red::before    { background: radial-gradient(ellipse at 50% 0%, #e74c3c, transparent 70%); }
.page-glow--yellow::before { background: radial-gradient(ellipse at 50% 0%, #f1c40f, transparent 70%); }
.page-glow--green::before  { background: radial-gradient(ellipse at 50% 0%, #2ecc71, transparent 70%); }
.page-glow--blue::before   { background: radial-gradient(ellipse at 50% 0%, #3498db, transparent 70%); }

/* Card glow — soft colour bleed behind cards */
.card-glow {
  position: relative;
}
.card-glow::before {
  content: '';
  position: absolute;
  inset: -8px;
  border-radius: 1.5rem;
  filter: blur(24px);
  opacity: 0.12;
  z-index: -1;
}
.card-glow--red::before    { background: #e74c3c; }
.card-glow--yellow::before { background: #f1c40f; }
.card-glow--green::before  { background: #2ecc71; }
.card-glow--blue::before   { background: #3498db; }
.card-glow--purple::before { background: #6c5ce7; }

/* Range slider styling */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
  width: 100%;
}
input[type="range"]::-webkit-slider-track {
  height: 4px;
  background: rgba(255,255,255,0.1);
  border-radius: 2px;
}
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #6c5ce7;
  margin-top: -8px;
  box-shadow: 0 0 8px rgba(108,92,231,0.4);
}
input[type="range"]::-moz-range-track {
  height: 4px;
  background: rgba(255,255,255,0.1);
  border-radius: 2px;
}
input[type="range"]::-moz-range-thumb {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #6c5ce7;
  border: none;
  box-shadow: 0 0 8px rgba(108,92,231,0.4);
}

/* Card system — the core visual pattern */
.card-dark {
  background: #161820;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 1.25rem;
}

.card-light {
  background: #ffffff;
  color: #1a1a2e;
  border-radius: 1.25rem;
}

.card-glass {
  background: rgba(255,255,255,0.04);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 1.25rem;
}

/* Softer borders globally */
.border-gray-800 { border-color: rgba(255,255,255,0.06) !important; }
.border-gray-700 { border-color: rgba(255,255,255,0.1) !important; }

/* Rounder corners globally */
.rounded-2xl { border-radius: 1.25rem !important; }
.rounded-xl { border-radius: 1rem !important; }

/* Smoother bg transitions */
.bg-gray-900 { background: #161820 !important; }
.bg-gray-950 { background: #0b0b0f !important; }
.bg-gray-800 { background: #1e2028 !important; }

/* ── Page background ── */
.page-bg {
  background: #0b0b0f;
  color: #ffffff;
}

/* ── Modal surface ── */
.modal-surface {
  background: #161820;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 1.25rem;
}

/* ── Input surface ── */
.input-surface {
  background: #1e2028;
  border-color: rgba(255,255,255,0.08);
  color: #ffffff;
}

/* ── Light mode overrides ─────────────────────────────── */
html.light, html.light body {
  background: #f8f9fa;
  color: #111827;
}

/* Page background */
html.light .page-bg {
  background: #f8f9fa !important;
  color: #111827 !important;
}

/* Card system */
html.light .card-dark {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}
html.light .modal-surface {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}
html.light .input-surface {
  background: #f3f4f6 !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}

/* Tailwind bg overrides */
html.light .bg-gray-950,
html.light .bg-\[\#0b0b0f\] {
  background: #f8f9fa !important;
}
html.light .bg-gray-900 {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}
html.light .bg-gray-800 {
  background: #f3f4f6 !important;
}
html.light .bg-gray-700 {
  background: #e5e7eb !important;
}

/* Inline style overrides — background: #161820 */
html.light [style*="background: #161820"],
html.light [style*="background:#161820"] {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}
html.light [style*="background: linear-gradient(135deg, rgba(167,139,250"] {
  background: linear-gradient(135deg, rgba(167,139,250,0.06), rgba(231,76,60,0.04)) !important;
  border-color: rgba(167,139,250,0.2) !important;
}

/* Text colours */
html.light .text-white { color: #111827 !important; }
html.light .text-gray-300 { color: #374151 !important; }
html.light .text-gray-400 { color: #6b7280 !important; }
html.light .text-gray-500 { color: #9ca3af !important; }
html.light .text-gray-600 { color: #9ca3af !important; }
html.light .text-gray-700 { color: #9ca3af !important; }

/* Border colours */
html.light .border-gray-800,
html.light .border-gray-700 {
  border-color: #e5e7eb !important;
}
html.light .border-white\/5,
html.light .border-white\/8,
html.light .border-white\/10 {
  border-color: #e5e7eb !important;
}

/* Semi-transparent white backgrounds flip to semi-transparent black */
html.light .bg-white\/5,
html.light .bg-white\/\[0\.03\],
html.light .bg-white\/\[0\.04\] {
  background: rgba(0,0,0,0.03) !important;
}
html.light .bg-white\/10 {
  background: rgba(0,0,0,0.06) !important;
}

/* Background with opacity */
html.light .bg-gray-900\/80,
html.light .bg-gray-900\/95 {
  background: rgba(255,255,255,0.95) !important;
}

/* Branded backgrounds — keep but soften */
html.light .bg-purple-500\/20 { background: rgba(108,92,231,0.08) !important; }
html.light .bg-purple-900\/20 { background: rgba(108,92,231,0.04) !important; }
html.light .bg-purple-500\/10 { background: rgba(108,92,231,0.06) !important; }

/* Ring and outline colours */
html.light .ring-white\/10 { --tw-ring-color: rgba(0,0,0,0.1) !important; }

/* Inputs and textareas */
html.light .placeholder-gray-500::placeholder,
html.light .placeholder-gray-600::placeholder {
  color: #9ca3af !important;
}
html.light input, html.light textarea {
  color: #111827 !important;
}
html.light input::placeholder, html.light textarea::placeholder {
  color: #9ca3af !important;
}

/* Modals and overlays */
html.light .bg-black\/70 { background: rgba(0,0,0,0.3) !important; }
html.light .bg-black\/80 { background: rgba(0,0,0,0.35) !important; }

/* NavBar */
html.light .nav-surface {
  background: rgba(255,255,255,0.95) !important;
  border-color: #e5e7eb !important;
}

/* Coach chat bubbles */
html.light .bg-\[\#161820\] {
  background: #f3f4f6 !important;
}
html.light .bg-\[\#0b0b0f\] {
  background: #f8f9fa !important;
}

/* Coach full-screen */
html.light .coach-bg {
  background: #f8f9fa !important;
}

/* Cards */
html.light .card-light { background: #ffffff !important; }
html.light .card-glass { background: rgba(255,255,255,0.7) !important; border-color: #e0e0ea !important; }

/* Dividers */
html.light .divide-white\/5 > :not([hidden]) ~ :not([hidden]) {
  border-color: #e5e7eb !important;
}

/* Scrollbar */
html.light ::-webkit-scrollbar-track { background: #f3f4f6; }
html.light ::-webkit-scrollbar-thumb { background: #d1d5db; }
html.light ::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

/* Glow effects — muted in light mode */
html.light body::before { opacity: 0.02; }
html.light .page-glow::before { opacity: 0.03; }
html.light .card-glow::before { opacity: 0.04; }

/* Range slider in light */
html.light input[type="range"]::-webkit-slider-track { background: rgba(0,0,0,0.1); }
html.light input[type="range"]::-moz-range-track { background: rgba(0,0,0,0.1); }

/* Dashed borders */
html.light [style*="border: 1px dashed rgba(255,255,255"] {
  border-color: #d1d5db !important;
}

/* Connected dots on integrations */
html.light [style*="border: 2px solid #161820"],
html.light [style*="border-2 border-[#161820]"] {
  border-color: #ffffff !important;
}

/* AnalysisSkeleton */
html.light .skeleton-bg {
  background: #f8f9fa !important;
}

/* CTA buttons: white bg in dark mode becomes brand purple in light mode */
html.light .bg-white.text-\[\#0b0b0f\] {
  background: #6c5ce7 !important;
  color: #ffffff !important;
}
html.light .bg-white.text-\[\#0b0b0f\]:hover {
  background: #5a4dd6 !important;
}

/* Active filter pills — in light mode, invert for contrast */
html.light button.bg-white.text-\[\#0b0b0f\] {
  background: #6c5ce7 !important;
  color: #ffffff !important;
}

/* User message bubbles in coach chat — keep white in both modes */
html.light .bg-white.text-\[\#0b0b0f\].ml-auto {
  background: #6c5ce7 !important;
  color: #ffffff !important;
}

/* QuizView PrimeVue textarea */
html.light .p-inputtextarea,
html.light textarea.p-inputtext {
  background: #f3f4f6 !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}

/* Fix inline box-shadow borders used on some elements */
html.light [style*="boxShadow: 0 0 0 2.5px"] {
  /* These are per-color avatar rings — they work fine in both modes */
}

/* Semi-transparent color backgrounds — keep in both modes */
html.light .bg-red-500\/15,
html.light .bg-yellow-500\/15,
html.light .bg-green-500\/15,
html.light .bg-blue-500\/15 {
  /* These are used for icon backgrounds and work in both modes */
}

/* Ensure modals have proper shadow in light mode */
html.light .modal-surface {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
}
html.light .card-dark {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
</style>
